import {useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {PageHeading} from '@/components/page-heading'
import {SectionCard} from '@/components/section-card'
import {Campo, ChipOpcion} from '@/components/campo'
import {ReadOnlyNotice} from '@/components/read-only-notice'
import {HelpIcon} from '@/components/help-icon'
import {Hint} from '@/components/ui/hint'
import {PorQue} from '@/components/ui/por-que'
import {EmployeeSingleSelect, type PersonaSeleccionable} from '@/components/employee-single-select'
import {AfectadoQuickCreateSlot} from '@/components/afectado-quick-create-slot'
import {useAllEmployees, useMyEmployee} from '@/hooks/empresa/identidad/use-employees'
import {
  useAfectadoPersonas,
  useReportarIncidente,
  type IncidenteGravedad,
  type IncidenteTipo,
} from '@/hooks/sgsst/incidentes/use-incidentes'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {translateApiError} from '@/lib/errors'
import {localToday} from '@/lib/risk'
import {tope} from '@/lib/campos-texto'

const GRAVEDADES: {value: IncidenteGravedad; tone?: string}[] = [
  {value: 'leve'},
  {value: 'grave', tone: 'tone-amber'},
  {value: 'mortal', tone: 'tone-red'},
]

type AfectadoModo = 'empleado' | 'externo'

// O8.1: espejo cliente de `core/incidentes.py::dentro_de_ventana_reporte` en días calendario, sin festivos
// en el navegador; el backend valida la ventana real. Umbral holgado: FURAT 2 días hábiles, investigación 15.
const VENTANA_DIAS_CALENDARIO: Record<IncidenteTipo, number> = {accidente: 4, incidente: 15}

function eventoEsViejo(tipo: IncidenteTipo, fecha: string): boolean {
  if (!fecha) return false
  // nosemgrep: browser-clock-business-date -- ventana-vencida check off the browser clock (deliberate client mirror of the backend's dentro_de_ventana_reporte, see file comment above); tracked debt, not fixed in this wave (FE-02)
  const dias = Math.floor((Date.parse(localToday()) - Date.parse(fecha)) / 86_400_000)
  return dias > VENTANA_DIAS_CALENDARIO[tipo]
}

/** Cualquier miembro reporta (D-2), en el sitio y con el celular: el afectado es del roster (cierra el
 * vínculo con el jefe inmediato) o externo por nombre. */
export function IncidenteReportarPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const navigate = useNavigate()
  const {data: me} = useMyEmployee(businessId)
  // El selector sale del camino recortado (nombre + área), que cualquier miembro puede
  // leer; el roster completo se queda para el alta rápida, que sí es de operadores.
  const {data: personas} = useAfectadoPersonas(businessId)
  const {data: roster, isError: rosterDenegado} = useAllEmployees(businessId)
  const reportar = useReportarIncidente(businessId!)
  const godReadOnly = useGodModeReadOnly()

  const [tipo, setTipo] = useState<IncidenteTipo>('accidente')
  const [gravedad, setGravedad] = useState<IncidenteGravedad>('leve')
  const [descripcion, setDescripcion] = useState('')
  const [afectadoModo, setAfectadoModo] = useState<AfectadoModo>('empleado')
  const [afectadoEmpleado, setAfectadoEmpleado] = useState<PersonaSeleccionable | null>(null)
  const [afectadoExterno, setAfectadoExterno] = useState('')
  // nosemgrep: browser-clock-business-date -- date input default value, legitimate data-entry (not a compliance decision)
  const [fecha, setFecha] = useState(localToday())
  const [hora, setHora] = useState('')
  const [lugar, setLugar] = useState('')
  const [reportadoArl, setReportadoArl] = useState(false)
  const [radicado, setRadicado] = useState('')
  const [error, setError] = useState<string | null>(null)

  const usaEmpleado = afectadoModo === 'empleado'
  const yaSoyElAfectado = !!me?.id && afectadoEmpleado?.id === me.id
  const esViejo = eventoEsViejo(tipo, fecha)
  // A fresh event never carries the «ya reportado por fuera» flag (even if the user
  // had marked it and then changed the date to today).
  const reportadoArlEfectivo = esViejo && reportadoArl

  function submit() {
    setError(null)
    reportar.mutate(
      {
        tipo,
        gravedad_reportada: gravedad,
        descripcion_hechos: descripcion.trim(),
        afectado_employee_id: usaEmpleado ? (afectadoEmpleado?.id ?? null) : null,
        afectado_externo_nombre: usaEmpleado ? null : afectadoExterno.trim() || null,
        evento_fecha: fecha,
        evento_hora: hora || null,
        lugar_libre: lugar.trim() || null,
        reportado_arl_externamente: reportadoArlEfectivo,
        radicado_externo: reportadoArlEfectivo ? radicado.trim() || null : null,
        radicado_externo_fecha: reportadoArlEfectivo ? fecha : null,
      },
      {
        // `nuevo=1` enciende en el detalle el aviso de qué se activó y qué sigue.
        onSuccess: (inc) => navigate(`/businesses/${businessId}/incidentes/${inc.id}?nuevo=1`),
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  const afectadoValido = usaEmpleado ? afectadoEmpleado !== null : afectadoExterno.trim().length > 0
  const valido = descripcion.trim().length > 0 && afectadoValido && fecha.length > 0
  const faltan = [
    descripcion.trim().length > 0 ? null : t('incidentes.reportar.faltaQue'),
    afectadoValido ? null : t('incidentes.reportar.faltaQuien'),
    fecha.length > 0 ? null : t('incidentes.reportar.faltaCuando'),
  ].filter((x): x is string => x !== null)

  return (
    <div className="space-y-5" data-testid="incidente-reportar">
      <PageHeading title={t('incidentes.reportar.title')} />

      <div className="mx-auto max-w-lg space-y-5">
        {godReadOnly && (
          <ReadOnlyNotice testId="incidente-reportar-readonly-banner">
            {t('incidentes.reportar.readOnlyBanner')}
          </ReadOnlyNotice>
        )}

        <SectionCard title={t('incidentes.reportar.bloqueMinimo')} bodyClassName="space-y-5 p-5">
          <Campo
            label={t('incidentes.reportar.tipoLabel')}
            accion={
              <PorQue
                label={t('incidentes.reportar.tipoHelpCta')}
                ariaLabel={t('incidentes.reportar.tipoHelpLabel')}
                testId="incidente-tipo-help"
                contenidoTestId="incidente-tipo-help-contenido">
                <p className="font-sans text-xs font-semibold text-foreground normal-case">
                  {t('incidentes.tipo.accidente')}
                </p>
                <p className="font-sans text-xs normal-case">{t('incidentes.reportar.tipoHelpAccidente')}</p>
                <p className="mt-2 font-sans text-xs font-semibold text-foreground normal-case">
                  {t('incidentes.tipo.incidente')}
                </p>
                <p className="font-sans text-xs normal-case">{t('incidentes.reportar.tipoHelpIncidente')}</p>
              </PorQue>
            }>
            <div className="grid grid-cols-2 gap-2" role="group" aria-label={t('incidentes.reportar.tipoLabel')}>
              {(['accidente', 'incidente'] as IncidenteTipo[]).map((value) => (
                <ChipOpcion
                  key={value}
                  data-testid={`incidente-tipo-${value}`}
                  seleccionado={tipo === value}
                  onClick={() => setTipo(value)}>
                  {t(`incidentes.tipo.${value}`)}
                </ChipOpcion>
              ))}
            </div>
          </Campo>

          <Campo label={t('incidentes.reportar.queLabel')} htmlFor="incidente-descripcion">
            <Textarea
              maxLength={tope('TEXTO')}
              id="incidente-descripcion"
              value={descripcion}
              data-testid="incidente-descripcion"
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder={t('incidentes.reportar.quePlaceholder')}
              minRows={3}
            />
          </Campo>

          <Campo label={t('incidentes.reportar.quienLabel')}>
            <div className="grid grid-cols-2 gap-2" role="group" aria-label={t('incidentes.reportar.quienLabel')}>
              {(['empleado', 'externo'] as AfectadoModo[]).map((value) => (
                <ChipOpcion
                  key={value}
                  data-testid={`incidente-afectado-modo-${value}`}
                  seleccionado={afectadoModo === value}
                  onClick={() => setAfectadoModo(value)}>
                  {t(`incidentes.reportar.afectadoModo.${value}`)}
                </ChipOpcion>
              ))}
            </div>

            {usaEmpleado ? (
              <div className="space-y-2">
                {me?.id && !yaSoyElAfectado && (
                  <Button
                    type="button"
                    variant="secondary"
                    data-testid="incidente-afectado-yo"
                    onClick={() => setAfectadoEmpleado(me)}
                    aria-label={t('incidentes.reportar.afectadoYoAriaLabel')}
                    className="min-h-11 w-full sm:w-auto">
                    {t('incidentes.reportar.afectadoYo')}
                  </Button>
                )}
                <EmployeeSingleSelect<PersonaSeleccionable>
                  roster={personas ?? []}
                  value={afectadoEmpleado}
                  onChange={setAfectadoEmpleado}
                  testId="incidente-afectado-empleado"
                />
                <AfectadoQuickCreateSlot
                  businessId={businessId!}
                  roster={roster ?? []}
                  rosterDenegado={rosterDenegado}
                  oculto={!!afectadoEmpleado || godReadOnly}
                  onCreated={setAfectadoEmpleado}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Input
                  maxLength={tope('NOM')}
                  type="text"
                  value={afectadoExterno}
                  data-testid="incidente-afectado-externo"
                  onChange={(e) => setAfectadoExterno(e.target.value)}
                  placeholder={t('incidentes.reportar.afectadoExternoPlaceholder')}
                  className="h-11 sm:h-9"
                />
                <Hint testId="incidente-afectado-externo-hint">{t('incidentes.reportar.afectadoExternoHint')}</Hint>
              </div>
            )}
          </Campo>

          <div className="grid grid-cols-2 gap-3">
            <Campo label={t('incidentes.reportar.cuandoLabel')} htmlFor="incidente-fecha">
              <Input
                id="incidente-fecha"
                type="date"
                value={fecha}
                data-testid="incidente-fecha"
                // nosemgrep: browser-clock-business-date -- date input max, legitimate data-entry (not a compliance decision)
                max={localToday()}
                onChange={(e) => setFecha(e.target.value)}
                className="h-11 font-mono tabular-nums sm:h-9"
              />
            </Campo>
            <Campo label={t('incidentes.reportar.horaLabel')} htmlFor="incidente-hora" opcional>
              <Input
                id="incidente-hora"
                type="time"
                value={hora}
                data-testid="incidente-hora"
                onChange={(e) => setHora(e.target.value)}
                className="h-11 font-mono tabular-nums sm:h-9"
              />
            </Campo>
          </div>

          {esViejo && (
            <div
              className="surface-divider space-y-2 rounded-xl border border-dashed bg-secondary/40 p-3"
              data-testid="incidente-retroactivo">
              <label className="flex min-h-11 items-start gap-2.5 sm:min-h-0">
                <input
                  type="checkbox"
                  checked={reportadoArl}
                  data-testid="incidente-reportado-arl"
                  onChange={(e) => setReportadoArl(e.target.checked)}
                  className="mt-0.5 size-4 shrink-0 accent-primary"
                />
                <span className="text-sm font-medium">{t('incidentes.reportar.yaReportadoArl')}</span>
              </label>
              <HelpIcon
                label={t('common.help.iconLabelFor', {campo: t('incidentes.reportar.yaReportadoArl')})}
                testId="incidente-reportado-arl-tip">
                {t('incidentes.reportar.yaReportadoArlTip')}
              </HelpIcon>
              {reportadoArl && (
                <Campo
                  className="pl-6"
                  label={t('incidentes.reportar.radicadoHistoricoLabel')}
                  htmlFor="incidente-radicado-externo"
                  opcional>
                  <Input
                    maxLength={tope('CODE')}
                    id="incidente-radicado-externo"
                    type="text"
                    value={radicado}
                    data-testid="incidente-radicado-externo"
                    onChange={(e) => setRadicado(e.target.value)}
                    placeholder={t('incidentes.reportar.radicadoHistoricoPlaceholder')}
                    className="h-11 font-mono sm:h-9"
                  />
                </Campo>
              )}
            </div>
          )}
        </SectionCard>

        <SectionCard title={t('incidentes.reportar.bloqueContexto')} bodyClassName="space-y-5 p-5">
          <Campo label={t('incidentes.reportar.dondeLabel')} htmlFor="incidente-lugar" opcional>
            <Input
              maxLength={tope('CIU')}
              id="incidente-lugar"
              type="text"
              value={lugar}
              data-testid="incidente-lugar"
              onChange={(e) => setLugar(e.target.value)}
              placeholder={t('incidentes.reportar.dondePlaceholder')}
              className="h-11 sm:h-9"
            />
          </Campo>

          <Campo label={t('incidentes.reportar.gravedadLabel')} hint={t('incidentes.reportar.gravedadHint')}>
            <div className="grid grid-cols-3 gap-2" role="group" aria-label={t('incidentes.reportar.gravedadLabel')}>
              {GRAVEDADES.map(({value, tone}) => (
                <ChipOpcion
                  key={value}
                  data-testid={`incidente-gravedad-${value}`}
                  seleccionado={gravedad === value}
                  tone={tone}
                  onClick={() => setGravedad(value)}
                  className="min-h-14">
                  {t(`incidentes.gravedad.${value}`)}
                </ChipOpcion>
              ))}
            </div>
          </Campo>
        </SectionCard>

        {error && (
          <p className="text-sm text-destructive" data-testid="incidente-reportar-error">
            {error}
          </p>
        )}

        {/* `main` es el contenedor con scroll: `sticky` se ancla ahí y no se mete debajo del rail. */}
        <div className="sticky bottom-0 -mx-4 border-t border-border bg-background/95 px-4 py-3 backdrop-blur-sm md:static md:mx-0 md:border-0 md:bg-transparent md:p-0 md:backdrop-blur-none">
          <Button
            className="w-full"
            size="lg"
            data-testid="incidente-reportar-submit"
            disabled={!valido || reportar.isPending || godReadOnly}
            onClick={submit}>
            {t('incidentes.reportar.submit')}
          </Button>
          {faltan.length > 0 && (
            <p className="mt-2 text-center text-xs text-muted-foreground" data-testid="incidente-reportar-falta">
              {t('common.falta', {campos: faltan.join(' · ')})}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
